import { createRouteFor } from '@components-common/routes';
import { presetsSection, presetsSectionBase } from '../routes.section';
import { ConfigSection } from '@components-fabric';
import { buildingParametersPresetsEntity } from './building-parameters-presets.entity';
import { PageTable } from '@components-common/layouts';
import { layoutWorkspace } from '../../../_website/_common/layout.workspace';
import { PresetCreatePage } from '../../../components-domain/generation-domain/presets/components/PresetCreatePage';
import { presetBuildingParametersCols } from '../../../components-domain/generation-domain/presets/components/_defaults';
import { PresetEditPage } from '../../../components-domain/generation-domain/presets/components/PresetEditPage';
import { ModalContentPrests } from '../../../components-domain/generation-domain/presets/components/ModalContentPresets';
import { compDBAdditionalCheck } from '../../../components-domain/bim-domain/defaults';
const basePath = presetsSectionBase;

/**
 * Configuration Object: editScreen
 *
 * This object defines the configuration for the edit screen of preset building parameters.
 * It specifies the component, props, and various data sources for fetching and updating data.
 *
 * @property {Array} children - An array of child configurations for the edit screen.
 */
const editScreen = {
  children: [
    {
      Component: PresetEditPage,
      props: {
        title: 'Edit Preset Building Parameters',
        itemFieldForTable: 'buildingParameter',
        toolbar: {
          cat: 'edit',
          left: ['save'],
          right: ['back'],
        },
        form: {
          element: {},
          schema: {},
          mode: 'edit',
        },
        getSourceTable:
          '/buildingms/presets-building-parameters/building-parameters-with-data',
        updateSourceForm: '/buildingms/presets/',
        deleteSource: '/buildingms/presets-building-parameters',
        modalProps: {
          Component: ModalContentPrests,
          title: 'Add more building parameters',
          show: true,
          actions: [],
          props: {
            selectedIdField: 'buildingParameterId',
            getSource: `/buildingms/categories/parameters/get-parameters-by-preset-id/`,
            createSource: `/buildingms/presets/building-parameters`,
            columns: presetBuildingParametersCols,
          },
        },
      },
    },
  ],
};

/**
 * Configuration Object: buildingParametersSection
 *
 * This object defines the configuration for the building parameters presets section.
 * It includes the section ID, title, path, layout, and an array of screen configurations.
 *
 * @property {string} id - The ID of the section.
 * @property {string} title - The title of the section.
 * @property {string} path - The URL path for the section.
 * @property {React.Component} layout - The layout component for the section.
 * @property {Array} screens - An array of screen configuration objects.
 */
export const buildingParametersSection: ConfigSection = {
  id: 'building_parameters_presets',
  title: 'Preset Building Parameters',
  path: 'building-parameters',
  layout: layoutWorkspace,
  screens: [
    {
      id: 'index',
      path: '.',
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      tmp: 'index',
      columns: buildingParametersPresetsEntity.columns,
      source: buildingParametersPresetsEntity.endpoints,
      urlParams: { type: 'BUILDING_PARAMETERS' },
      actions: [
        {
          id: 'edit',
          actionToDispatch: { payload: { path: '' } },
          additionalChecks: compDBAdditionalCheck,
          displayTooltip: true,
          tooltipText: 'Edit preset',
        },
        {
          id: 'copy',
          actionToDispatch: {
            payload: {
              copy: '/buildingms/presets/clonePreset',
            },
          },
          displayTooltip: true,
          tooltipText: 'Duplicate preset',
        } as any,
        {
          id: 'delete',
          actionToDispatch: {
            payload: {
              endpointToDelete: '/buildingms/presets',
            },
          },
          additionalChecks: compDBAdditionalCheck,
          displayTooltip: true,
          tooltipText: 'Delete preset',
        },
      ],
      Component: PageTable,
    } as any,
    {
      id: 'create',
      path: 'new',
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      tmp: 'custom',
      children: [
        {
          Component: PresetCreatePage,
          props: {
            title: 'Create Preset Building Parameters',
            form: {
              element: {
                name: '',
                description: '',
                type: 'BUILDING_PARAMETERS',
              },
              schema: {},
              mode: 'create',
            },
            schema: {},
            columns: presetBuildingParametersCols,
            getPresetItemsSource:
              '/buildingms/categories/parameters?category=BUILDING',
            createPresetItemsSource: '/buildingms/presets/building-parameters',
            navUrl: '/components-db/presets/building-parameters',
          },
        },
      ],
    },
    {
      id: 'edit',
      path: ':presetId',
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      tmp: 'custom',
      ...editScreen,
    },
  ],
};

/**
 * Function: createRouteFor
 *
 * This function generates routes for the building parameters presets section.
 *
 * @param {ConfigSection} presetsSection - The presets section configuration.
 * @param {string} routeKey - The key for the route.
 *
 * @returns {Object} An object containing the generated routes for building parameters presets.
 */
const routesForSpaceFunctions = createRouteFor<
  typeof presetsSection,
  'building_parameters'
>(presetsSection, 'building_parameters');

/**
 * Object: buildingParametersPresetsRoutes
 *
 * This object defines the routes for the building parameters presets section.
 *
 * @property {Object} index - The route configuration for the index page.
 * @property {Object} create - The route configuration for the create page.
 * @property {Object} details - The route configuration for the details page.
 */
export const buildingParametersPresetsRoutes = {
  index: routesForSpaceFunctions('index', { basePath }),
  create: routesForSpaceFunctions('create', { basePath }),
  details: routesForSpaceFunctions('details', { basePath }),
};

/**
 * Type: BuildingParametersRoutesIds
 *
 * This type defines the IDs for the building parameters presets routes.
 *
 * @typedef {string} BuildingParametersRoutesIds - The ID of the building parameters presets route.
 */
export type BuildingParametersRoutesIds =
  typeof buildingParametersPresetsRoutes[keyof typeof buildingParametersPresetsRoutes]['id'];
